export interface UpdateLogEntry {
  version: string;
  date: string;
  changes: string[];
}

export interface BuildMetadata {
  version: string | null;
  gitRevision: string | null;
  environment: string | null;
  buildTime: string | null;
}

export type RevisionStatus = 'synchronized' | 'mismatch' | 'unavailable';

export function buildMetadataValue(value?: string | null): string {
  return value?.trim() ? value : 'Unavailable';
}

export function classifyRevisionStatus(
  webRevision?: string | null,
  apiRevision?: string | null,
): RevisionStatus {
  if (!webRevision?.trim() || !apiRevision?.trim()) return 'unavailable';
  return webRevision === apiRevision ? 'synchronized' : 'mismatch';
}

export function webBuildMetadata(): BuildMetadata {
  return {
    version: __APP_VERSION__,
    gitRevision: __GIT_REVISION__,
    environment: __BUILD_ENVIRONMENT__,
    buildTime: __BUILD_TIME__,
  };
}

export type VersionStatusKind = 'synchronized' | 'update-available' | 'mismatch' | 'unavailable';

export interface VersionStatus {
  kind: VersionStatusKind;
  webVersion: string;
  apiVersion?: string;
}

/** Returns true when version `a` is strictly greater than `b` (major.minor.patch). */
export function isNewerVersion(a: string, b: string): boolean {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff > 0) return true;
    if (diff < 0) return false;
  }
  return false;
}

export function classifyVersionStatus(webVersion: string, apiVersion?: string): VersionStatus {
  if (!apiVersion) return { kind: 'unavailable', webVersion };
  if (apiVersion === webVersion) return { kind: 'synchronized', webVersion, apiVersion };
  if (isNewerVersion(apiVersion, webVersion)) {
    return { kind: 'update-available', webVersion, apiVersion };
  }
  return { kind: 'mismatch', webVersion, apiVersion };
}

export const UPDATE_LOG: UpdateLogEntry[] = [
  {
    version: '2.7.1',
    date: '2026-10-21',
    changes: [
      '現金開帳與日結：整合現金袋實點、預備金補充、賽事收入交接及保險箱存入紀錄。',
      '帳務概覽：每日結餘顯示四袋現金，賽事收入顯示日結扣除補款後存入保險箱的金額。',
      '結算來源：揭露參賽人數、R／A 次數、費用與保留 Z 點及實發獎金；零用金與入會費依各自來源顯示明細。',
      '會員錢包：改善 ZIPA、OP 與 Z 點手動異動的營業日、來源及歷史餘額顯示；帳務明細聚焦相關資產。',
      '入會費：LINE 自助新註冊比照後台新增顯示 100 元現金入會費，依開帳及權限條件確認收款。',
      '會員來源：匯入或既有會員重新綁定 LINE 不建立額外收費來源，顯示不重複收費提示並保留原有收款紀錄。',
      '會員列表：移除 ZIPA 每日櫃檯合計區塊，保留搜尋、篩選、匯出與會員資料操作。',
    ],
  },
  {
    version: '2.7.0',
    date: '2026-10-08',
    changes: [
      '會員管理：整合會員資格與等級、堂票、票券及月友方案，並提供發放、扣用、沖銷與歷史紀錄。',
      '會員錢包：會員中心可查看有效堂票、票券與使用紀錄，並保留場館及有效期限資訊。',
      '會員標籤：提供標籤管理、指派與匯入匯出，依操作權限與場館範圍顯示。',
      '會員設定：整理等級、票券、堂票與月友設定入口；會員中心編輯器同步目前主題與會員卡預覽。',
      '會員資料：修正新增會員失敗及批次匯入的翻譯與數量顯示。',
      'ZIPA 與點數：整合已交付的扣用規則、帳本類型與期初匯入工具；實際資料轉入仍需另行確認。',
      '計時器總時長：暫停時停止累計，恢復或重新連線後接續有效運行時間。',
      '休息提醒：距離休息倒數加入跑馬燈，接在賽事模板訊息後輪播。',
      '電視與預覽：LEVEL、長盲注、小時計時與統計文字依欄位空間自動縮放，減少換行及截斷。',
      '計時器版面：BLINDS/BBA 與 NEXT LEVEL 的標籤、數值和提示改為緊密置中排列。',
      '計時器音效：可設定八種事件提示音與音量（含靜音），首次啟動仍保留固定提示聲。',
      '版本基準：彙整已完成會員功能，作為後續系統頁面、介面與操作流程優化的基準版本。',
    ],
  },
  {
    version: '2.6.9',
    date: '2026-10-05',
    changes: [
      '賽事時鐘版面：縮短標題、盲注與倒數資訊間距，改善電視顯示。',
      '賽事時鐘音效：更新升盲與倒數提示音；休息前 10 秒播放提示、移除 30 秒提示，並避免重連或重複更新重播音效。',
      '盲注結構：補上 BBA 清單欄位，修正 SB、BB、Ante、BBA 與時間設定欄位對齊。',
      '計時器控制台：Ante/BBA 顯示目前與下一級的設定值。',
      '電視與預覽盲注：有 Ante 或 BBA 時，以 BLINDS/BBA 顯示合併數值；未設定時維持原始顯示，Next Level 資訊保持不變。',
    ],
  },
  {
    version: '2.6.8',
    date: '2026-09-30',
    changes: [
      '賽事套件：匯入匯出可包含限時賽結算級別與派彩規則，保留舊版套件相容性，匯入前可預覽內容。',
      '限時賽結算：改善桌位點算、籌碼紀錄、結算預覽與確認流程，並支援結算收據分頁列印。',
      '會員帳本：新增選手點數、限時賽點數、Z 點數與 ZIPA 帳本分頁，結算更正可呈現欠點餘額。',
      '會員資料：新增 CRM 匯入預覽、會員查重與黑名單提示，保留匯入來源紀錄。',
      '會員中心：新增會員卡、歷史紀錄與個人資料頁面，並提供 LINE LIFF 入口。',
      '後台導覽：調整系統管理選單順序與點數設定名稱。',
    ],
  },
  {
    version: '2.6.7',
    date: '2026-09-26',
    changes: [
      '電視配對：改善顯示器工作階段的持續連線與重新配對流程。',
      '帳號安全：強化雙因素驗證資料保護、Redis 撤銷檢查、稽核紀錄與高權限帳號限制。',
      '公開資訊：縮減未登入時可取得的版本、裝置及會員資料，移除舊版裝置端點。',
      '會員查重：限制查詢頻率並統一回應，避免以查重功能推測會員是否存在。',
    ],
  },
  {
    version: '2.6.6',
    date: '2026-09-21',
    changes: [
      '電視顯示安全性：改用短效且可撤銷的螢幕工作階段，賽事切換由伺服器綁定，並限制 WebSocket 來源。',
      '服務自動復原：移除可存取 Docker socket 的 autoheal 容器，改由 API 容器內健康監控配合重啟政策復原。',
      '部署安全性：正式與預備環境拒絕停用雙因素驗證或啟用 E2E 測試管理員，並在 CI 加入秘密掃描。',
      '賽事時鐘提示：升盲時播放提示音；休息前 30 秒與 10 秒使用不同高頻提示，最後一級倒數結束播放三次提示。',
      '電視盲注資訊：下一階段為休息時顯示休息後第一個可玩級別的 SB／BB／Ante；最後一級以「—」顯示無下一級。',
    ],
  },
  {
    version: '2.6.5',
    date: '2026-09-16',
    changes: ['修復跨場館操作權限與手機排版優化'],
  },
  {
    version: '2.6.4',
    date: '2026-09-14',
    changes: [
      '計時器控制台：新增增加／減少 1、3、5 分鐘及自訂秒數功能，調整時保留目前盲注級別與運行／暫停狀態，並防止扣減至無效時間。',
      '賽事時長顯示：限時賽顯示包含休息時間的固定總時長；淘汰賽與賞金賽顯示自開賽起的經過時間，包含暫停與休息，並於賽事完成後固定最終時長。',
      '休息倒數顯示：休息期間保留完整賽事時鐘版面，在原倒數區域以紅底白字顯示 Break 與剩餘時間。',
      '限時賽結算流程：最後一級倒數結束後保留 10 分鐘緩衝，期滿自動暫停並進入結算中狀態，提示工作人員點算籌碼；手動調整時間可取消待執行的自動結算。',
      '開賽前電視配置：草稿與開放報名階段即可配置電視，預先顯示第一級盲注、完整倒數及「尚未開始」，正式開賽前倒數保持不動。',
      '手機版儀表板：快速入口調整為「建立賽事、賽事列表、電視顯示、收支登記」，採雙欄排列；進行中賽事上移，方便查看並直接進入計時器。',
      '賽事詳情手機版：優化直向小螢幕的標題、資訊與按鈕配置，主要操作依序為「開賽、控制台、配置電視」，提升觸控便利性。',
      '電視配置操作：從賽事詳情開啟配置電視時，直接顯示賽事指派視窗，背景保留原詳情頁面。',
    ],
  },
  {
    version: '2.6.3',
    date: '2026-09-12',
    changes: [
      'Improved multi-venue member onboarding while keeping venue operations independent.',
      'Added portable tournament template and blind structure import and export workflows.',
      'Added voice-assisted member registration with an explicit feature gate.',
    ],
  },
];
